/* eslint-disable @typescript-eslint/no-explicit-any */
import { env } from '@/utils/configs/env';
import {
  CustomCellProperties,
  TableInstance,
  TableLowcode,
  TableSpec,
} from 'alurkerja-ui';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  IFilterSpec,
  TableFilterPanel,
  TableFilterPanelRef,
} from '@/components/shared/filter/TableFilterPanel';

export type ExtendedTableLowCodeRef = {
  refetch: () => void;
  hide: () => void;
  show: () => void;
  getFilter: () => { [x: string]: any } | undefined;
  getSort: () => string | undefined;
  getOrderBy: () => 'asc' | 'desc' | undefined;
  filter: (data: { [x: string]: any }) => void;
  setSelectedAll(data: boolean): void;
};

type ExtendedTableLowCodeProps = {
  spec: TableSpec;
  defaultSort?: string;
  defaultSortOrder?: 'asc' | 'desc';
  defaultFilter?: {
    [x: string]: any;
  };
  subHeader?: () => React.ReactNode;
  customCell?: ({
    name,
    fields,
    value,
    rowValue,
    defaultCell,
  }: CustomCellProperties) => React.ReactElement;
  customActionCell?: (data: { [x: string]: any }) => React.ReactElement;
  customButtonCreate?: (
    ButtonWithModal: React.ReactElement,
    ButtonWithAction: React.ReactElement,
    data?: {
      [x: string]: any;
    }[],
  ) => React.ReactElement;
  showCheckbox?: boolean;
  showFilter?: boolean;
  hideActionColumn?: boolean;
  hidden?: boolean;
  filterSpec?: Array<IFilterSpec>;
  selectedRow?: any[];
  setSelectedRow?: React.Dispatch<React.SetStateAction<any[]>>;
};

const HideCustomButtonBulk = () => <></>;

export const ExtendedTableLowCode = forwardRef<
  ExtendedTableLowCodeRef,
  ExtendedTableLowCodeProps
>((props, ref) => {
  const filterRef = useRef<TableFilterPanelRef>(null);
  const tableRef = useRef<TableInstance>(null);
  const [pageConfig, setPageConfig] = useState({ limit: 10, page: 0 });
  const [filter, setFilter] = useState(props.defaultFilter);
  const [renderState, setRenderState] = useState(0);
  const [sort, setSort] = useState<string | undefined>(props.defaultSort);
  const [orderBy, setOrderBy] = useState<'asc' | 'desc' | undefined>(
    props.defaultSortOrder,
  );
  const [hide, setHide] = useState(props.hidden ?? false);

  useImperativeHandle(ref, () => ({
    refetch: handleRefetch,
    filter: handleFilter,
    getFilter: handleGetFilter,
    getSort: () => sort,
    getOrderBy: () => orderBy,
    hide: () => setHide(true),
    show: () => setHide(false),
    setSelectedAll: handleSetSelectedAll,
  }));

  const handleRefetch = () => setRenderState(prev => prev + 1);

  const handleFilter = (data: { [x: string]: any }) => {
    setFilter({ ...data, ...props.defaultFilter });
  };

  const handleGetFilter = () => {
    return { ...filter, ...props.defaultFilter };
  };

  const handleSetSelectedAll = (value: boolean) => {
    tableRef.current?.setSelectedAll(value);
  };

  const buildSubHeader = (): React.ReactNode | undefined => {
    if (props.showFilter) {
      return (
        <TableFilterPanel
          ref={filterRef}
          filterSpecs={props.filterSpec ?? []}
          onSubmit={data => {
            // encode data
            const result: { [x: string]: any } = {};
            for (const [key, value] of Object.entries(data)) {
              if (value) {
                result[key] = encodeURIComponent(value);
              }
            }

            setFilter(result);
            setPageConfig({ limit: 10, page: 0 }); // Reset page config
          }}
        />
      );
    }

    return props.subHeader?.();
  };

  return hide ? (
    <></>
  ) : (
    <TableLowcode
      baseUrl={env.apiBaseUrl}
      spec={props.spec}
      canBulk={props.showCheckbox}
      pageConfig={pageConfig}
      setPageConfig={setPageConfig}
      filterBy={{ ...filter, ...props.defaultFilter }}
      setFilterBy={setFilter}
      renderState={renderState}
      setRenderState={setRenderState}
      sortBy={sort}
      setSortBy={setSort}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
      subHeader={buildSubHeader}
      customCell={props.customCell}
      customActionCell={props.customActionCell}
      customButtonCreate={props.customButtonCreate}
      hideActionColumn={props.hideActionColumn}
      customButtonBulk={HideCustomButtonBulk}
      onClickFilter={() => {
        filterRef?.current?.toggle?.();
      }}
      customButtonFilter={(_, ButtonComponent) =>
        props.showFilter ? (
          <ButtonComponent
            type="button"
            onClick={() => filterRef?.current?.toggle?.()}
          />
        ) : (
          <></>
        )
      }
      selectedRow={props.selectedRow}
      setSelectedRow={props.setSelectedRow}
    />
  );
});
